import { Alert, Linking, Platform } from "react-native";
import jwt from "jwt-decode";
import Config from "react-native-config";
import Storage from "./storage/StorageFunctions";
import Constants, {
  BRAND_NAMES,
  getCompanyName,
  isGradientAllowed,
  ScreenNameList,
} from "./Constants";
import { store } from "../store/Store";
import { updateUser } from "../store/slices/UserSlice";
import { getUserDetailsFromId } from "../api/user";
import { messages } from "../../src/resources/strings";
import { theme } from "../../src/theme/theme";
import RNBlobUtil from "../../src/components/rn-blob/RNBlobUtil";

import RNFS from "../../src/components/RNFS/RNFS";
import RNShare from "../../src/components/RNShare/RNShare";
import DeviceInfo from "react-native-device-info";
import { getFormattedLabelText } from "./Formatters";

type RecordObject<T> = { [k: string]: T };

export const Logger = (data: any) => {
  let env =
    Platform.OS === "web" ? process.env.REACT_APP_ENV : Config.REACT_APP_ENV;
  if (env === "prod") {
    return;
  } else {
    console.log(data);
  }
};

export function getUnique(array: any[], key: any) {
  if (typeof key !== "function") {
    const property = key;
    key = function (item: any) {
      return item[property];
    };
  }
  return Array.from(
    array
      .reduce(function (map, item) {
        const k = key(item);
        if (!map.has(k)) map.set(k, item);
        return map;
      }, new Map())
      .values(),
  );
}

export const userDisplayName = async (user: any) => {
  if (typeof user === "undefined" || user == null) {
    return null;
  }
  const userProfile = user.profile;
  let displayName = "";
  if (userProfile.first_name !== "" && userProfile.first_name !== null) {
    displayName = userProfile.first_name;
  }
  if (userProfile.last_name !== "" && userProfile.last_name !== null) {
    displayName = displayName + " " + userProfile.last_name;
  }
  if (displayName === "" && user.email != null && user.email.length > 0) {
    displayName = user.email[0].email ?? "";
  }
  return displayName;
};

export const getUserEmail = async () => {
  const user = store.getState().userReducer;
  let email = "";
  if (user.email != null && user.email.length > 0) {
    email = user.email[0].email ?? "";
  }
  return email;
};

export const fetchUserEmail = async () => {
  const tokenLS = await Storage.getData(
    Constants.LocalStoreConstant.ACCESS_TOKEN,
  );
  if (typeof tokenLS !== "undefined") {
    let decoded: any = jwt(tokenLS.split(" ")[1]);
    const object: any = await getUserDetailsFromId(decoded?.sub);
    if (object.errorCode === messages.error.noError) {
      store.dispatch(updateUser(object.response.user));
      return getUserEmail();
    }
  }
};

export const getUserIdFromToken = (token: string) => {
  if (token == null) {
    return null;
  }
  let decoded: any = jwt(token.split(" ")[1]);
  return decoded?.sub ?? "";
};

export const getUserProfile = async () => {
  const tokenLS = await Storage.getData(
    Constants.LocalStoreConstant.ACCESS_TOKEN,
  );
  if (typeof tokenLS !== "undefined") {
    let decoded: any = jwt(tokenLS.split(" ")[1]);
    if (decoded) {
      const object: any = await getUserDetailsFromId(decoded?.sub);
      if (object.errorCode === messages.error.noError) {
        store.dispatch(updateUser(object.response.user));
        return userDisplayName(object.response.user);
      }
    }
  }
};

export const getBackgroundColor = (screenName: string, isLight?: boolean) => {
  if (isLight) {
    switch (screenName) {
      case ScreenNameList.contactAgent:
        return theme.colors.grayLight;
      case ScreenNameList.makeClaim:
        return theme.colors.secondaryLight;
      case ScreenNameList.documentInfo:
        return theme.colors.primaryLight;
      case ScreenNameList.billingPayments:
        return theme.colors.primaryLight;
      default:
        return theme.colors.gray;
    }
  }
  switch (screenName) {
    case ScreenNameList.contactAgent:
      if (isGradientAllowed) {
        return theme.colors.secondary;
      } else {
        return theme.colors.gray;
      }
    case ScreenNameList.makeClaim:
      return theme.colors.secondary;
    case ScreenNameList.documentInfo:
      return theme.colors.primary;
    case ScreenNameList.billingPayments:
      return theme.colors.primary;
    default:
      return theme.colors.gray;
  }
};
export const filterObjectByCondition = <T extends Record<string, any>>(
  data: RecordObject<T>,
  condition: (value: T, key: string) => boolean,
): RecordObject<T> => {
  return Object.fromEntries(
    Object.entries(data).filter(([key, value]) => condition(value, key)),
  ) as RecordObject<T>;
};

export const sortObjectByKey = <T extends Record<string, any>>(
  data: RecordObject<T>,
  key: keyof T,
): RecordObject<T> => {
  return Object.fromEntries(
    Object.entries(data).sort(([, a], [, b]) => {
      const valA = String(a[key] ?? "");
      const valB = String(b[key] ?? "");
      return valA.localeCompare(valB, undefined, { sensitivity: "base" });
    }),
  ) as RecordObject<T>;
};

export const getEnv = () => {
  let env =
    Platform.OS === "web" ? process.env.REACT_APP_ENV : Config.REACT_APP_ENV;
  return env;
};

export function formatPortalOneError(payload: any): string {
  const p = payload || {};
  const desc =
    p.Description || p.message || p.error || "Payment error occurred";

  if (p.Details && typeof p.Details === "object") {
    try {
      const details = Object.entries(p.Details)
        .map(([k, v]) => {
          if (v == null) return `${k}: ${v}`;
          if (Array.isArray(v)) return `${k}: ${v.join(", ")}`;
          if (typeof v === "object") return `${k}: ${JSON.stringify(v)}`;
          return `${k}: ${v}`;
        })
        .join("; ");
      return `${desc} (${details})`;
    } catch (e) {
      return desc;
    }
  }

  return desc;
}

export function getPaymentInfo(
  nextPayment: string | null | undefined,
  balanceDue: string | null | undefined,
) {
  const toPositiveNumber = (value?: string | null): number | null => {
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? num : null;
  };

  if (toPositiveNumber(nextPayment)) {
    return nextPayment;
  }

  if (toPositiveNumber(balanceDue)) {
    return balanceDue;
  }

  return -1;
}

const checkandSetToast = (
  message: string,
  setErrorHandle: (data: string) => void,
) => {
  if (
    message?.includes("SaveToFiles") ||
    message?.includes("MarkupAsPDF") ||
    message?.includes("Print")
  ) {
    setErrorHandle(messages.success.downloadSuccess);
    return;
  }
};

export async function DownloadUsingManager(
  fileUrl: string,
  fileName: string,
  setErrorHandle: (data: string) => void,
  setLoading: (data: boolean) => void,
  setDownloadLoader: (val: boolean) => void,
) {
  if (RNFS) {
    const accessToken = await Storage.getData(
      Constants.LocalStoreConstant.ACCESS_TOKEN,
    );
    try {
      if (Platform.OS === "android") {
        const { config, fs } = RNBlobUtil;
        const downloads = fs.dirs.LegacyDownloadDir;
        // setLoading(true);
        config({
          fileCache: true,
          addAndroidDownloads: {
            useDownloadManager: true,
            notification: true,
            path: `${downloads}/${fileName}`,
            mime: "application/pdf",
            title: fileName,
            mediaScannable: true,
          },
        })
          .fetch("GET", fileUrl, {
            Authorization: accessToken,
            "Content-Type": "application/pdf",
          })
          .then((res) => {
            setLoading(false);
            setDownloadLoader(false);
            setErrorHandle(messages.success.downloadSuccess);
          })
          .catch((err) => {
            setLoading(false);
            setDownloadLoader(false);
            setErrorHandle(err);
          });
      } else if (Platform.OS === "ios") {
        try {
          const token = await Storage.getData(
            Constants.LocalStoreConstant.ACCESS_TOKEN,
          );

          const destPath = `${RNFS?.DocumentDirectoryPath ?? ""}/${fileName}`;
          setLoading(true);
          const downloadResult = await RNFS.downloadFile({
            fromUrl: fileUrl,
            toFile: destPath,
            headers: {
              Authorization: token,
            },
          }).promise;
          setLoading(false);
          setDownloadLoader(false);
          if (
            downloadResult.statusCode === 200 ||
            downloadResult.statusCode === 201
          ) {
            try {
              const result = await RNShare.open({
                url: "file://" + destPath,
                type: "application/pdf",
                excludedActivityTypes: [
                  "com.apple.UIKit.activity.AssignToContact",
                  "com.apple.UIKit.activity.AddToReadingList",
                  "com.apple.UIKit.activity.Print",
                  "com.apple.UIKit.activity.SaveToCameraRoll",
                ],
              });
              setLoading(false);
              setDownloadLoader(false);
              if (Platform.OS === "ios") {
                let resultMsg = result?.message ?? "";
                checkandSetToast(resultMsg, setErrorHandle);
              } else {
                setErrorHandle(messages.success.downloadSuccess);
              }
            } catch (e) {
              console.log(messages.error.downloadFailedTryAgain);
            }
          } else if (downloadResult.statusCode === 500) {
            setErrorHandle(messages.error.downloadFailedTryAgain);
          }
        } catch (err: any) {
          setErrorHandle(err.toString());
          setDownloadLoader(false);
          console.error("iOS file handling error", err);
        }
      }
    } catch (e) {
      setLoading(false);
      setDownloadLoader(false);
      console.error("DownloadUsingManager error", e);
    }
  }
}

export const replaceMamtInUrl = (
  url: string,
  amount: number | string,
): string => {
  try {
    const [base, query = ""] = url.split("?");
    const params = query.split("&").filter(Boolean);

    let hasMamt = false;

    const updatedParams = params.map((param) => {
      const [key] = param.split("=");

      if (key === "mamt") {
        hasMamt = true;
        return `mamt=${encodeURIComponent(String(amount))}`;
      }

      return param;
    });

    if (!hasMamt) {
      updatedParams.push(`mamt=${encodeURIComponent(String(amount))}`);
    }

    return `${base}?${updatedParams.join("&")}`;
  } catch (error) {
    console.log("Failed to replace mamt:", error);
    return url;
  }
};

export const handleIOSPaymentEvent = (event: any) => {
  const { url, status, scheme } = event.nativeEvent;
  return url;
};

export const getPrimaryPolicyHolderId = (
  policyHolders: any[],
  email: string,
) => {
  const primaryPolicyHolder = policyHolders.find(
    (holder) => holder.emailAddress == email,
  );
  if (!primaryPolicyHolder) {
    return policyHolders[0]?.id ?? "";
  }
  return primaryPolicyHolder?.id ?? "";
};

export const getStoreUrl = (target?: "android" | "ios") => {
  if (target == "android") {
    // Navigate to Android Store
    return (
      (process.env.REACT_APP_ANDROID_STORE_URL
        ? process.env.REACT_APP_ANDROID_STORE_URL
        : Config.REACT_APP_ANDROID_STORE_URL) ?? ""
    );
  } else if (target == "ios") {
    // Navigate to iOS Store
    return (
      (process.env.REACT_APP_IOS_STORE_URL
        ? process.env.REACT_APP_IOS_STORE_URL
        : Config.REACT_APP_IOS_STORE_URL) ?? ""
    );
  } else {
    return DeviceInfo.getBaseOs().then((device) => {
      if (
        device.toLocaleLowerCase() == "ios" ||
        device.toLocaleLowerCase() == "iphone" ||
        device.toLocaleLowerCase() == "mac os"
      ) {
        return (
          (process.env.REACT_APP_IOS_STORE_URL
            ? process.env.REACT_APP_IOS_STORE_URL
            : Config.REACT_APP_IOS_STORE_URL) ?? ""
        );
      } else {
        return (
          (process.env.REACT_APP_ANDROID_STORE_URL
            ? process.env.REACT_APP_ANDROID_STORE_URL
            : Config.REACT_APP_ANDROID_STORE_URL) ?? ""
        );
      }
    });
  }
};

export const createVisitRequestBody = async (
  ref: any,
  eventType = "page_view",
  storeUrl?: string,
) => {
  const baseInfo = await DeviceInfo.getBaseOs();
  return {
    carrier: getCompanyName(),
    eventType: eventType,
    userAgent: await DeviceInfo.getUserAgent(),
    deviceInfo: {
      brand: DeviceInfo.getBrand(),
      model: DeviceInfo.getModel(),
      systemName: DeviceInfo.getSystemName(),
      systemVersion: DeviceInfo.getSystemVersion(),
      uniqueId: await DeviceInfo.getUniqueId(),
      isTablet: DeviceInfo.isTablet(),
      storeUrl: storeUrl,
    },
    layout: DeviceInfo.isTablet() ? "Tablet" : "Mobile",
    storeTarget: baseInfo == "android" ? "Android" : "iOS",
    ref: ref || null,
    path: "/shareable",
  };
};

export const setAnimationLoaded = (value: boolean) => {
  document.cookie = `animationLoaded=${value};max-age=604800;path=/`;
};

export const isAnimationLoaded = () => {
  const cookie = document.cookie
    .split("; ")
    .find((c) => c.startsWith("animationLoaded="));

  if (!cookie) return false;

  const value = cookie.split("=")[1];
  return value === "true";
};
export const appUpdateRequired = (current: string, incoming: string) => {
  const currentParts = current.split(".").map(Number);
  const incomingParts = incoming.split(".").map(Number);

  for (
    let i = 0;
    i < Math.max(currentParts.length, incomingParts.length);
    i++
  ) {
    const currentVersion = currentParts[i] || 0;
    const newVersion = incomingParts[i] || 0;
    if (currentVersion < newVersion) return true;
    if (currentVersion > newVersion) return false;
  }
  return false;
};

export const openAppStore = async () => {
  const APP_STORE_URL = await getStoreUrl(
    Platform.OS == "android" ? "android" : "ios",
  );
  const supported = await Linking.canOpenURL(APP_STORE_URL);
  if (supported) {
    await Linking.openURL(APP_STORE_URL);
  } else {
    console.log("Cannot open App Store");
  }
};

export const getEnvironment = () => {
  const env =
    Platform.OS === "web" ? process.env.REACT_APP_ENV : Config.REACT_APP_ENV;

  if (env === "prod") {
    return "";
  } else if (env === "stage") {
    return "stage.";
  } else {
    return "demo.";
  }
};

export const getConfiguredBrandName = (companyName?: string): string => {
  if (!companyName) return "";

  if (companyName === BRAND_NAMES.HCPCI) return "Homeowners Choice";
  if (companyName === BRAND_NAMES.TYPTAP) return "TypTap";
  if (companyName === BRAND_NAMES.CORE) return companyName.toUpperCase();

  return getFormattedLabelText(companyName);
};

export const getRedirectUri = () => {
  if (Platform.OS === "android") {
    if (Config.REACT_APP_REDIRECT_URI) {
      return Config.REACT_APP_REDIRECT_URI;
    }
    return `https://policy.${getEnvironment()}${getCompanyName().toLocaleLowerCase()}.com/login`;
  } else return "";
};